import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Image as ImageIcon, Info, Settings as SettingsIcon, Play } from 'lucide-react';
import { ProcessorSelector } from './ProcessorSelector';
import { ProcessorSettings } from './ProcessorSettings';
import ImageComparisonView from './ImageComparisonView';
import { useImageProcessorWithPreview } from './hooks/useImageProcessorWithPreview';

interface ProcessorInfo {
  name: string;
  description: string;
  version: string;
  supported_formats: string[];
  parameters: Record<string, any>;
}

const ImageProcessor: React.FC = () => {
  const [availableProcessors, setAvailableProcessors] = useState<Record<string, ProcessorInfo>>({});
  const [selectedProcessor, setSelectedProcessor] = useState<string>('compress');
  const [isLoadingProcessors, setIsLoadingProcessors] = useState(true);
  
  const {
    file,
    setFile,
    settings,
    setSettings,
    result,
    isProcessing,
    error,
    processImage,
    downloadProcessedImage,
    clearResult,
    clearError,
    previewState,
    onImageMetadata,
    onPreviewError
  } = useImageProcessorWithPreview();

  useEffect(() => {
    loadAvailableProcessors();
  }, []);

  const loadAvailableProcessors = async () => {
    try {
      setIsLoadingProcessors(true);
      const response = await fetch('/api/tools/image-processor/processor/processors');
      const data = await response.json();
      setAvailableProcessors(data);
      
      // 如果compress处理器存在，默认选择它
      if (data.compress) {
        setSelectedProcessor('compress');
      } else {
        // 否则选择第一个可用的处理器
        const firstProcessor = Object.keys(data)[0];
        if (firstProcessor) {
          setSelectedProcessor(firstProcessor);
        }
      }
    } catch (err) {
      console.error('加载处理器失败:', err);
    } finally {
      setIsLoadingProcessors(false);
    }
  };

  const handleProcessorChange = useCallback((processorName: string) => {
    setSelectedProcessor(processorName);
    clearResult();
    clearError();
    
    // 重置设置为默认值
    const processor = availableProcessors[processorName];
    if (processor) {
      const defaultSettings: Record<string, any> = {};
      Object.entries(processor.parameters).forEach(([key, param]) => {
        if (param.default !== undefined) {
          defaultSettings[key] = param.default;
        }
      });
      setSettings(defaultSettings);
    }
  }, [availableProcessors, clearResult, clearError, setSettings]);

  const handleProcessImage = useCallback(async () => {
    if (!file || !selectedProcessor) return;
    
    await processImage(selectedProcessor, settings);
  }, [file, selectedProcessor, processImage, settings]);

  const handleDownload = useCallback(() => {
    if (!file || !selectedProcessor) return;
    downloadProcessedImage(selectedProcessor, settings);
  }, [file, selectedProcessor, downloadProcessedImage, settings]);

  const currentProcessor = availableProcessors[selectedProcessor];

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      {/* 页面标题 */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ImageIcon className="w-5 h-5" />
            通用图片处理工具
          </CardTitle>
          <CardDescription>
            支持多种图片处理功能的模块化处理平台，左右对比查看处理效果
          </CardDescription>
        </CardHeader>
      </Card>

      {/* 主要内容区域 */}
      <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
        
        {/* 左侧控制面板 */}
        <div className="xl:col-span-1 space-y-4">
          
          {/* 处理器选择 */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center">
                <SettingsIcon className="w-4 h-4 mr-2" />
                选择处理器
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ProcessorSelector
                processors={availableProcessors}
                selectedProcessor={selectedProcessor}
                onProcessorChange={handleProcessorChange}
                isLoading={isLoadingProcessors}
              />
            </CardContent>
          </Card>

          {/* 处理器设置 */}
          {file && currentProcessor && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center">
                  <SettingsIcon className="w-4 h-4 mr-2" />
                  处理设置
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ProcessorSettings
                  processor={currentProcessor}
                  settings={settings}
                  onSettingsChange={setSettings}
                />
              </CardContent>
            </Card>
          )}

          {/* 处理按钮 */}
          {file && currentProcessor && (
            <Card>
              <CardContent className="pt-6">
                <Button
                  onClick={handleProcessImage}
                  disabled={isProcessing}
                  className="w-full"
                  size="lg"
                >
                  {isProcessing ? (
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      处理中...
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Play className="w-4 h-4" />
                      开始处理
                    </div>
                  )}
                </Button>
              </CardContent>
            </Card>
          )}

          {/* 处理器信息 */}
          {currentProcessor && (
            <Card className="bg-blue-50 border-blue-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-blue-800 flex items-center gap-2 text-sm">
                  <Info className="w-4 h-4" />
                  处理器信息
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div>
                  <span className="font-medium text-blue-700">名称:</span>
                  <span className="ml-2 text-blue-900">{currentProcessor.name}</span>
                </div>
                <div>
                  <span className="font-medium text-blue-700">描述:</span>
                  <span className="ml-2 text-blue-900">{currentProcessor.description}</span>
                </div>
                <div>
                  <span className="font-medium text-blue-700">版本:</span>
                  <span className="ml-2 text-blue-900">{currentProcessor.version}</span>
                </div>
                <div>
                  <span className="font-medium text-blue-700">支持格式:</span>
                  <div className="ml-2 flex flex-wrap gap-1 mt-1">
                    {currentProcessor.supported_formats.map(format => (
                      <span key={format} className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
                        {format.toUpperCase()}
                      </span>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* 右侧图片对比区域 */}
        <div className="xl:col-span-3">
          
          {/* 错误显示 */}
          {(error || previewState.previewError) && (
            <Card className="mb-4 bg-red-50 border-red-200">
              <CardContent className="pt-4">
                {error && <p className="text-red-700 mb-2">处理错误: {error}</p>}
                {previewState.previewError && (
                  <p className="text-red-700">预览错误: {previewState.previewError}</p>
                )}
              </CardContent>
            </Card>
          )}

          {/* 图片对比显示组件 */}
          <ImageComparisonView
            originalFile={file}
            onFileChange={setFile}
            processedResult={result}
            isProcessing={isProcessing}
            supportedFormats={currentProcessor?.supported_formats || []}
            onImageLoad={onImageMetadata}
            onError={onPreviewError}
            onDownload={handleDownload}
          />
        </div>
      </div>
    </div>
  );
};

export default ImageProcessor;